"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useMemo } from "react";
import { Tag, Truck, Check } from "lucide-react";
import Carousel from "react-multi-carousel";
import { getDeviceType } from "@/utils/device";
export interface VoucherCardInfo {
  id: string | number;
  headline: string;
  subtext: string;
  type?: "coupon" | "shipping" | "general";
  code?: string;
}
export const parseVoucherCards = (product: any): VoucherCardInfo[] => {
  if (!product) return [];
  const cards: VoucherCardInfo[] = [];
  const seenCodes = new Set<string>();

  // 1. Process applicable_vouchers array if present
  if (Array.isArray(product.applicable_vouchers) && product.applicable_vouchers.length > 0) {
    product.applicable_vouchers.forEach((v: any, index: number) => {
      if (typeof v === "object" && v !== null) {
        const code = v.code ? v.code.toString().trim() : "";
        const rawVal = v.discount_value ?? v.discount_amount ?? v.value;
        const val = rawVal !== undefined && rawVal !== null ? parseFloat(rawVal.toString()) : null;
        const type = v.discount_value_type || v.discount_type;
        const maxDiscount = v.max_discount_value || v.max_discount;
        const minSpend = v.min_order_value ?? v.min_spent_amount;
        let headline = "";
        if (val !== null && !isNaN(val)) {
          if (type === "percentage") {
            headline = `${val}% OFF`;
            if (maxDiscount && parseFloat(maxDiscount.toString()) > 0) {
              headline += ` UPTO ${parseFloat(maxDiscount.toString())}`;
            }
          } else if (type === "fixed" || type === "flat" || type === "amount") {
            headline = `FLAT ₹${val} OFF`;
          } else {
            headline = `${val} OFF`;
          }
        } else if (v.headline || v.title || v.name) {
          headline = (v.headline || v.title || v.name).toString().trim();
        }
        let subtext = "";
        if (code) {
          subtext = `Use Code: ${code}`;
        } else if (minSpend && parseFloat(minSpend.toString()) > 0) {
          subtext = `On orders above ₹${parseFloat(minSpend.toString())}`;
        } else if (v.subtext || v.description) {
          subtext = (v.subtext || v.description).toString().trim();
        } else {
          subtext = "Applicable at checkout";
        }
        if (headline) {
          if (code) seenCodes.add(code.toLowerCase());
          cards.push({
            id: v.id || `voucher-${index}`,
            headline,
            subtext,
            type: "coupon",
            code
          });
        }
      } else if (typeof v === "string" && v.trim()) {
        const text = v.trim();
        const useMatch = text.match(/(.*?)\s+USE\s+(.*)/i);
        if (useMatch && useMatch[1] && useMatch[2]) {
          const code = useMatch[2].trim();
          seenCodes.add(code.toLowerCase());
          cards.push({
            id: `voucher-str-${index}`,
            headline: useMatch[1].trim(),
            subtext: `Use Code: ${code}`,
            type: "coupon",
            code
          });
        } else {
          cards.push({
            id: `voucher-str-${index}`,
            headline: text,
            subtext: "Applicable at checkout",
            type: "coupon"
          });
        }
      }
    });
  }

  // 2. Fallback to vendor_discount_text if no vouchers in list
  if (cards.length === 0 && product.vendor_discount_text) {
    const text = product.vendor_discount_text.toString().trim();
    const useMatch = text.match(/(.*?)\s+USE\s+(.*)/i);
    if (useMatch && useMatch[1] && useMatch[2]) {
      const code = useMatch[2].trim();
      seenCodes.add(code.toLowerCase());
      cards.push({
        id: "vendor-discount-1",
        headline: useMatch[1].trim(),
        subtext: `Use Code: ${code}`,
        type: "coupon",
        code
      });
    } else {
      cards.push({
        id: "vendor-discount-1",
        headline: text,
        subtext: "Applicable at checkout",
        type: "coupon"
      });
    }
  }
  return cards;
};
const ACCENT_STYLES = [{
  borderLeft: "border-l-[#C29E30]",
  iconColor: "text-[#8B6E14]"
}, {
  borderLeft: "border-l-[#3CA697]",
  iconColor: "text-[#037A6A]"
}, {
  borderLeft: "border-l-[#A64F8F]",
  iconColor: "text-[#8A226F]"
}];
interface VoucherCarouselProps {
  product: any;
}
const CustomDot = t0 => {
  const $ = _c(5);
  const {
    onClick,
    ...rest
  } = t0;
  const {
    active
  } = rest;
  const t1 = `mx-1 transition-all duration-300 ease-out rounded-full ${active ? "w-5 h-1.5 bg-stone-500" : "w-1.5 h-1.5 bg-gray-300 hover:bg-gray-400"}`;
  let t2;
  if ($[0] !== onClick) {
    t2 = () => onClick();
    $[0] = onClick;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <button className={t1} onClick={t2} aria-label="Go to slide" />;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
const VoucherCarousel: React.FC<VoucherCarouselProps> = t0 => {
  const $ = _c(52);
  const {
    product
  } = t0;
  const [copiedCode, setCopiedCode] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getDeviceType();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const deviceType = t1;
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] !== copiedCode || $[2] !== product) {
    t17 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const cards = parseVoucherCards(product);
      if (cards.length === 0) {
        t17 = null;
        break bb0;
      }
      let t18;
      if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
        t18 = code => {
          if (!code) {
            return;
          }
          navigator.clipboard.writeText(code);
          setCopiedCode(code);
          setTimeout(() => {
            setCopiedCode(null);
          }, 2000);
        };
        $[20] = t18;
      } else {
        t18 = $[20];
      }
      const handleCopyCode = t18;
      let t19;
      if ($[21] !== copiedCode) {
        t19 = (card, index) => {
          const styleConfig = ACCENT_STYLES[index % ACCENT_STYLES.length];
          const isShipping = card.type === "shipping";
          const isCopied = card.code && copiedCode === card.code;
          return <div key={card.id || index} onClick={() => card.code && handleCopyCode(card.code)} className={`my-4 flex items-center gap-3 bg-white pl-4 pr-2 py-3 rounded-xl border border-gray-200/70 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border-l-[4px] ${styleConfig.borderLeft} w-full transition-all hover:shadow-md ${card.code ? "cursor-pointer" : ""}`}><div className="flex items-center justify-center flex-shrink-0">{isShipping ? <Truck className={`w-6 h-6 ${styleConfig.iconColor} stroke-[2]`} /> : <Tag className={`w-6 h-6 ${styleConfig.iconColor} stroke-[2] -rotate-45`} />}</div><div className="flex flex-col justify-center min-w-0 flex-1"><span className="font-bold text-sm sm:text-base text-[#4A4A4A] tracking-tight leading-snug truncate">{card.headline}</span><span className="text-xs text-[#5A5A5A] font-medium leading-tight mt-0.5 flex items-center gap-1 truncate">{isCopied ? <span className="text-green-600 font-semibold flex items-center gap-1"><Check className="w-3 h-3" /> Copied!</span> : card.subtext.startsWith("Use Code:") ? <><span className="text-[#8C8C8C] font-normal">Use Code:</span>{" "}<span>{card.subtext.replace("Use Code:", "").trim()}</span></> : card.subtext}</span></div></div>;
        };
        $[21] = copiedCode;
        $[22] = t19;
      } else {
        t19 = $[22];
      }
      const renderCardItem = t19;
      if (cards.length === 1) {
        t17 = <div className="w-full my-2"><div className="w-full max-w-[220px] sm:max-w-[300px]">{renderCardItem(cards[0], 0)}</div></div>;
        break bb0;
      }
      if (cards.length === 2) {
        let t20;
        if ($[23] !== renderCardItem) {
          t20 = (card_0, idx) => <div key={card_0.id || idx} className="flex-1 min-w-0">{renderCardItem(card_0, idx)}</div>;
          $[23] = renderCardItem;
          $[24] = t20;
        } else {
          t20 = $[24];
        }
        let t21;
        if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
          t21 = {
            desktop: {
              breakpoint: {
                max: 3000,
                min: 1024
              },
              items: 1,
              slidesToSlide: 1,
              partialVisibilityGutter: 0
            },
            tablet: {
              breakpoint: {
                max: 1024,
                min: 640
              },
              items: 1,
              slidesToSlide: 1,
              partialVisibilityGutter: 0
            },
            mobile: {
              breakpoint: {
                max: 640,
                min: 0
              },
              items: 1,
              slidesToSlide: 1,
              partialVisibilityGutter: 160
            }
          };
          $[25] = t21;
        } else {
          t21 = $[25];
        }
        let t22;
        if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
          t22 = <CustomDot />;
          $[26] = t22;
        } else {
          t22 = $[26];
        }
        let t23;
        if ($[27] !== renderCardItem) {
          t23 = (card_1, idx_0) => <div key={card_1.id || idx_0} className="w-full max-w-[220px] py-0.5 mx-auto">{renderCardItem(card_1, idx_0)}</div>;
          $[27] = renderCardItem;
          $[28] = t23;
        } else {
          t23 = $[28];
        }
        t17 = <div className="w-full my-2"><div className="hidden sm:flex flex-row items-center gap-3 w-full">{cards.map(t20)}</div><div className="block sm:hidden voucher-carousel-wrap"><Carousel partialVisible={true} responsive={t21} infinite={true} autoPlay={true} autoPlaySpeed={3500} arrows={false} showDots={true} renderDotsOutside={true} ssr={true} deviceType={deviceType} itemClass="px-0.5" dotListClass="flex items-center justify-center" customDot={t22}>{cards.map(t23)}</Carousel></div></div>;
        break bb0;
      }
      t16 = "w-full my-2 voucher-carousel-wrap";
      T0 = Carousel;
      t2 = true;
      if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = {
          desktop: {
            breakpoint: {
              max: 3000,
              min: 1024
            },
            items: 2,
            slidesToSlide: 2,
            partialVisibilityGutter: 0
          },
          tablet: {
            breakpoint: {
              max: 1024,
              min: 640
            },
            items: 2,
            slidesToSlide: 2,
            partialVisibilityGutter: 0
          },
          mobile: {
            breakpoint: {
              max: 640,
              min: 0
            },
            items: 1,
            slidesToSlide: 1,
            partialVisibilityGutter: 160
          }
        };
        $[29] = t3;
      } else {
        t3 = $[29];
      }
      t4 = true;
      t5 = true;
      t6 = 3500;
      t7 = false;
      t8 = true;
      t9 = true;
      t10 = true;
      t11 = deviceType;
      t12 = "px-1.5";
      t13 = "flex items-center justify-center";
      if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = <CustomDot />;
        $[30] = t14;
      } else {
        t14 = $[30];
      }
      let t20;
      if ($[31] !== renderCardItem) {
        t20 = (card_2, idx_1) => <div key={card_2.id || idx_1} className="w-full max-w-[220px] py-0.5 mx-auto">{renderCardItem(card_2, idx_1)}</div>;
        $[31] = renderCardItem;
        $[32] = t20;
      } else {
        t20 = $[32];
      }
      t15 = cards.map(t20);
    }
    $[1] = copiedCode;
    $[2] = product;
    $[3] = T0;
    $[4] = t10;
    $[5] = t11;
    $[6] = t12;
    $[7] = t13;
    $[8] = t14;
    $[9] = t15;
    $[10] = t16;
    $[11] = t17;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    T0 = $[3];
    t10 = $[4];
    t11 = $[5];
    t12 = $[6];
    t13 = $[7];
    t14 = $[8];
    t15 = $[9];
    t16 = $[10];
    t17 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  if (t17 !== Symbol.for("react.early_return_sentinel")) {
    return t17;
  }
  let t18;
  if ($[33] !== T0 || $[34] !== t10 || $[35] !== t11 || $[36] !== t12 || $[37] !== t13 || $[38] !== t14 || $[39] !== t15 || $[40] !== t2 || $[41] !== t3 || $[42] !== t4 || $[43] !== t5 || $[44] !== t6 || $[45] !== t7 || $[46] !== t8 || $[47] !== t9) {
    t18 = <T0 partialVisible={t2} responsive={t3} infinite={t4} autoPlay={t5} autoPlaySpeed={t6} arrows={t7} showDots={t8} renderDotsOutside={t9} ssr={t10} deviceType={t11} itemClass={t12} dotListClass={t13} customDot={t14}>{t15}</T0>;
    $[33] = T0;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
    $[39] = t15;
    $[40] = t2;
    $[41] = t3;
    $[42] = t4;
    $[43] = t5;
    $[44] = t6;
    $[45] = t7;
    $[46] = t8;
    $[47] = t9;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t16 || $[50] !== t18) {
    t19 = <div className={t16}>{t18}</div>;
    $[49] = t16;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  return t19;
};
export default VoucherCarousel;